import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const STATUSES = ["pending", "contacted", "responded", "converted"];
const STATUS_COLORS = {
  pending: "#6b7280",
  contacted: "#3b82f6",
  responded: "#f59e0b",
  converted: "#22c55e",
};

const getErrorMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  fallback;

export default function Outreach() {
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [newPlanName, setNewPlanName] = useState("");
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [copiedTemplate, setCopiedTemplate] = useState(null);

  const [tplForm, setTplForm] = useState({ name: "", category: "expand", subject: "", body: "" });
  const [editingTplId, setEditingTplId] = useState(null);
  const [showTplForm, setShowTplForm] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const [todoItems, setTodoItems] = useState([]);
  const [insights, setInsights] = useState(null);

  const loadTodo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/outreach/todo`, { headers: getHeaders() });
      setTodoItems(res.data.items || []);
    } catch (err) {
      console.error("Failed to load call list:", err);
    }
  }, []);

  useEffect(() => {
    loadTodo();
  }, [loadTodo]);

  const completeTodo = async (item) => {
    try {
      await axios.post(`${API}/outreach/todo/${item.id}/done`, {}, { headers: getHeaders() });
      setTodoItems((prev) => prev.filter((t) => t.id !== item.id));
    } catch (err) {
      console.error("Failed to complete call:", err);
    }
  };

  const showError = (message) => {
    setSuccessMessage("");
    setErrorMessage(message);
  };

  const showSuccess = (message) => {
    setErrorMessage("");
    setSuccessMessage(message);
  };

  const fetchPlans = useCallback(async () => {
    try {
      setLoadingPlans(true);
      const res = await axios.get(`${API}/outreach/plans`, { headers: getHeaders() });
      setPlans(res.data);
    } catch (error) {
      showError(getErrorMessage(error, "Failed to load outreach plans."));
    } finally {
      setLoadingPlans(false);
    }
  }, []);

  const fetchPlan = useCallback(async (id) => {
    try {
      setLoadingPlan(true);
      const res = await axios.get(`${API}/outreach/plans/${id}`, { headers: getHeaders() });
      setActivePlan(res.data);
      // Practice grouping + payer mix + penetrance (non-blocking)
      axios
        .get(`${API}/outreach/plans/${id}/insights`, { headers: getHeaders() })
        .then((r) => setInsights(r.data))
        .catch(() => setInsights(null));
    } catch (error) {
      if (error.response?.status === 404) {
        setActivePlanId(null);
        setActivePlan(null);
        await fetchPlans();
        showError("The selected outreach plan no longer exists.");
      } else {
        setActivePlan(null);
        showError(getErrorMessage(error, "Failed to load the selected outreach plan."));
      }
    } finally {
      setLoadingPlan(false);
    }
  }, [fetchPlans]);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const res = await axios.get(`${API}/outreach/templates`, { headers: getHeaders() });
      setTemplates(res.data);
    } catch (error) {
      showError(getErrorMessage(error, "Failed to load outreach templates."));
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
    fetchTemplates();
  }, [fetchPlans, fetchTemplates]);

  useEffect(() => {
    if (activePlanId) {
      fetchPlan(activePlanId);
    } else {
      setActivePlan(null);
      setInsights(null);
    }
  }, [activePlanId, fetchPlan]);

  useEffect(() => {
    const targets = activePlan?.targets || [];
    if (selectedTargetId && !targets.some((target) => target.id === selectedTargetId)) {
      setSelectedTargetId(null);
    }
  }, [activePlan, selectedTargetId]);

  useEffect(() => {
    if (!copiedTemplate) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setCopiedTemplate(null);
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [copiedTemplate]);

  const createPlan = async () => {
    if (!newPlanName.trim()) {
      showError("Plan name is required.");
      return;
    }

    try {
      setBusyAction("create-plan");
      const res = await axios.post(
        `${API}/outreach/plans`,
        { name: newPlanName },
        { headers: getHeaders() }
      );
      setNewPlanName("");
      await fetchPlans();
      setActivePlanId(res.data.id);
      showSuccess("Outreach plan created.");
    } catch (error) {
      showError(getErrorMessage(error, "Failed to create outreach plan."));
    } finally {
      setBusyAction("");
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Delete this outreach plan and all its targets?")) return;

    try {
      setBusyAction(`delete-plan-${id}`);
      await axios.delete(`${API}/outreach/plans/${id}`, { headers: getHeaders() });
      if (activePlanId === id) {
        setActivePlanId(null);
        setActivePlan(null);
      }
      await fetchPlans();
      showSuccess("Outreach plan deleted.");
    } catch (error) {
      showError(getErrorMessage(error, "Failed to delete outreach plan."));
    } finally {
      setBusyAction("");
    }
  };

  const updateTarget = async (targetId, data) => {
    try {
      setBusyAction(`target-${targetId}`);
      await axios.put(`${API}/outreach/targets/${targetId}`, data, { headers: getHeaders() });
      if (activePlanId) {
        await fetchPlan(activePlanId);
      }
    } catch (error) {
      showError(getErrorMessage(error, "Failed to update outreach target."));
    } finally {
      setBusyAction("");
    }
  };

  const removeTarget = async (targetId) => {
    try {
      setBusyAction(`target-${targetId}`);
      await axios.delete(`${API}/outreach/targets/${targetId}`, { headers: getHeaders() });
      if (activePlanId) {
        await fetchPlan(activePlanId);
      }
      showSuccess("Outreach target removed.");
    } catch (error) {
      showError(getErrorMessage(error, "Failed to remove outreach target."));
    } finally {
      setBusyAction("");
    }
  };

  const saveTpl = async () => {
    if (!tplForm.name.trim() || !tplForm.body.trim()) {
      showError("Template name and body are required.");
      return;
    }

    try {
      setBusyAction("save-template");
      if (editingTplId) {
        await axios.put(`${API}/outreach/templates/${editingTplId}`, tplForm, { headers: getHeaders() });
      } else {
        await axios.post(`${API}/outreach/templates`, tplForm, { headers: getHeaders() });
      }
      setTplForm({ name: "", category: "expand", subject: "", body: "" });
      setEditingTplId(null);
      setShowTplForm(false);
      await fetchTemplates();
      showSuccess(editingTplId ? "Template updated." : "Template created.");
    } catch (error) {
      showError(getErrorMessage(error, "Failed to save outreach template."));
    } finally {
      setBusyAction("");
    }
  };

  const deleteTpl = async (id) => {
    if (!window.confirm("Delete this outreach template?")) return;

    try {
      setBusyAction(`delete-template-${id}`);
      await axios.delete(`${API}/outreach/templates/${id}`, { headers: getHeaders() });
      await fetchTemplates();
      if (editingTplId === id) {
        setEditingTplId(null);
        setTplForm({ name: "", category: "expand", subject: "", body: "" });
        setShowTplForm(false);
      }
      showSuccess("Template deleted.");
    } catch (error) {
      showError(getErrorMessage(error, "Failed to delete outreach template."));
    } finally {
      setBusyAction("");
    }
  };

  const copyTemplate = async (tpl) => {
    try {
      const target = (activePlan?.targets || []).find((item) => item.id === selectedTargetId);
      let text = `Subject: ${tpl.subject || ""}\n\n${tpl.body}`;
      if (target?.referrer) {
        text = text.replace(/\{\{referrer_name\}\}/g, target.referrer.name || "");
        text = text.replace(/\{\{practice_name\}\}/g, "");
        text = text.replace(/\{\{your_name\}\}/g, "");
      }

      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard is not available in this browser.");
      }

      await navigator.clipboard.writeText(text);
      setSuccessMessage("");
      setErrorMessage("");
      setCopiedTemplate({ id: tpl.id, at: Date.now() });
    } catch (error) {
      showError(error.message || "Failed to copy outreach template.");
    }
  };

  const targets = activePlan?.targets || [];
  const expandTargets = targets.filter((target) => target.category === "expand");
  const bankTargets = targets.filter((target) => target.category === "bank");

  const formatPhone = (raw) => {
    if (!raw) return "—";
    const digits = String(raw).replace(/\D/g, "").slice(0, 10);
    return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : raw;
  };

  const renderTargetRow = (target) => {
    const referrer = target.referrer || {};
    const isTargetBusy = busyAction === `target-${target.id}`;
    return (
      <div key={target.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto auto auto", gap: "0.75rem", alignItems: "center", padding: "0.75rem", backgroundColor: "white", borderRadius: "6px", border: "1px solid #e5e7eb", marginBottom: "0.5rem" }}>
        <div>
          <div style={{ fontWeight: 600 }}>
            {referrer.name || "Unknown"}
            <span style={{ marginLeft: "0.4rem", fontSize: "0.68rem", padding: "0.1rem 0.45rem", borderRadius: "9999px", verticalAlign: "middle", backgroundColor: target.category === "bank" ? "#dcfce7" : target.category === "call" ? "#fef3c7" : "#dbeafe", color: target.category === "bank" ? "#15803d" : target.category === "call" ? "#92400e" : "#1d4ed8" }}>
              {target.category === "bank" ? "Bank" : target.category === "call" ? "Call" : "Expand"}
            </span>
          </div>
          <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{referrer.specialty || ""}</div>
        </div>
        <div style={{ fontSize: "0.85rem", color: "#4b5563" }}>
          {[referrer.city, referrer.state, referrer.zip_code].filter(Boolean).join(", ")}
        </div>
        <div style={{ fontSize: "0.85rem" }}>
          <div>{formatPhone(referrer.phone)}</div>
          {referrer.email && <a href={`mailto:${referrer.email}`} style={{ color: "#3b82f6", fontSize: "0.8rem" }}>{referrer.email}</a>}
        </div>
        <select
          value={target.status}
          disabled={isTargetBusy}
          onChange={(e) => updateTarget(target.id, { status: e.target.value })}
          style={{ padding: "0.25rem 0.5rem", borderRadius: "4px", border: "1px solid #d1d5db", color: STATUS_COLORS[target.status] || "#333", fontWeight: 600, fontSize: "0.8rem" }}
        >
          {STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
        <input
          type="radio"
          name="selectedTarget"
          checked={selectedTargetId === target.id}
          disabled={isTargetBusy}
          onChange={() => setSelectedTargetId(target.id)}
          title="Select for template"
          style={{ cursor: "pointer" }}
        />
        <button onClick={() => removeTarget(target.id)} disabled={isTargetBusy} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontWeight: 700, fontSize: "1.1rem", opacity: isTargetBusy ? 0.5 : 1 }} title="Remove">×</button>
      </div>
    );
  };

  const renderTargetGroup = (label, items, color) => (
    <div style={{ marginBottom: "1.5rem" }}>
      <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color, marginBottom: "0.5rem", borderBottom: `2px solid ${color}`, paddingBottom: "0.25rem" }}>
        {label} ({items.length})
      </h3>
      {items.length === 0 ? (
        <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>No targets yet. Add referrers from Radar or Dashboard.</p>
      ) : (
        items.map(renderTargetRow)
      )}
    </div>
  );

  const tierBadge = (mix) => {
    if (!mix) return null;
    const tiers = mix.tiers || {};
    const parts = [];
    if (tiers["Commercial-leaning"]) parts.push(`${tiers["Commercial-leaning"]} commercial-leaning`);
    if (tiers["Mixed"]) parts.push(`${tiers["Mixed"]} mixed`);
    if (tiers["Medicare-heavy"]) parts.push(`${tiers["Medicare-heavy"]} medicare-heavy`);
    return (
      <span>
        {mix.avg_private_payer_score != null && (
          <span style={{ fontWeight: 700, color: "#15803d" }}>Payer score {Math.round(mix.avg_private_payer_score)}</span>
        )}
        {parts.length > 0 && <span style={{ color: "#6b7280" }}>{mix.avg_private_payer_score != null ? " · " : ""}{parts.join(" · ")}</span>}
      </span>
    );
  };

  const renderPracticeGroups = () => {
    const targetById = new Map(targets.map((t) => [t.id, t]));
    return insights.practices.map((practice) => {
      const pen = practice.penetrance;
      return (
        <div key={practice.key} style={{ marginBottom: "1.5rem", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
          <div style={{ padding: "0.75rem 1rem", backgroundColor: "#f0fdf4", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: "0.5rem" }}>
              <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#14532d" }}>
                🏥 {practice.is_independent ? "Independent / solo providers" : practice.organization_name || "Practice"}
                <span style={{ fontWeight: 400, fontSize: "0.85rem", color: "#6b7280" }}>
                  {" "}· {practice.providers.length} in this plan
                  {practice.practice_size ? ` · ${practice.practice_size} providers at this practice` : ""}
                </span>
              </div>
              {pen && pen.penetrance_pct != null && (
                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#15803d" }} title="Providers at this practice already referring to you">
                  My penetrance: {Math.round(pen.penetrance_pct)}%
                  <span style={{ fontWeight: 400, color: "#6b7280" }}>
                    {" "}({pen.active} active{pen.prospect ? `, ${pen.prospect} prospect${pen.prospect !== 1 ? "s" : ""}` : ""} of {pen.practice_size})
                  </span>
                </div>
              )}
            </div>
            <div style={{ fontSize: "0.82rem", marginTop: "0.25rem" }}>{tierBadge(practice.payer_mix)}</div>
          </div>
          <div style={{ padding: "0.75rem" }}>
            {practice.providers.map((p) => {
              const target = targetById.get(p.target_id);
              return target ? renderTargetRow(target) : null;
            })}
          </div>
        </div>
      );
    });
  };

  const renderAreas = () => (
    <div style={{ marginBottom: "1.5rem" }}>
      <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#15803d", marginBottom: "0.5rem" }}>
        Target areas
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: "0.75rem" }}>
        {insights.areas.map((area) => {
          const pm = area.payer_mix || {};
          const pen = area.penetrance || {};
          return (
            <div key={area.zip} style={{ padding: "0.75rem", backgroundColor: "white", border: "1px solid #e5e7eb", borderRadius: "8px", fontSize: "0.82rem" }}>
              <div style={{ fontWeight: 700, marginBottom: "0.2rem" }}>
                ZIP {area.zip}
                <span style={{ fontWeight: 400, color: "#6b7280" }}>
                  {area.city ? ` · ${area.city}, ${area.state}` : ""}
                </span>
              </div>
              <div style={{ color: "#6b7280" }}>
                {area.targets_here} plan target{area.targets_here !== 1 ? "s" : ""} here
              </div>
              <div style={{ marginTop: "0.3rem" }}>
                {pm.private_coverage_rate != null && (
                  <div title="Share of residents with private insurance (Census ACS)">
                    <b style={{ color: "#15803d" }}>{Math.round(pm.private_coverage_rate)}%</b> privately insured
                    {pm.median_household_income != null && (
                      <span style={{ color: "#6b7280" }}> · ${Math.round(pm.median_household_income / 1000)}k income</span>
                    )}
                  </div>
                )}
                {pm.avg_private_payer_score != null && (
                  <div style={{ color: "#6b7280" }} title="Average Private Payer Score of providers officed in this ZIP">
                    Providers avg payer score {Math.round(pm.avg_private_payer_score)}
                  </div>
                )}
                {pen.penetrance_pct != null && (
                  <div title={`Of the top ${pen.top_n} providers officed here: ${pen.active_in_top} active, ${pen.prospect_in_top} prospects, ${pen.untapped_in_top} untapped`}>
                    My area penetrance: <b>{Math.round(pen.penetrance_pct)}%</b>
                    <span style={{ color: "#6b7280" }}> · {pen.untapped_in_top} untapped of top {pen.top_n}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{ padding: "1.5rem", maxWidth: "1100px", margin: "0 auto" }}>
      <h2 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#15803d", marginBottom: "1rem" }}>
        Outreach Plan Builder
      </h2>

      {todoItems.length > 0 && (
        <div style={{ marginBottom: "1.5rem", padding: "1rem", backgroundColor: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "8px" }}>
          <h3 style={{ fontWeight: 700, marginBottom: "0.6rem", color: "#92400e" }}>
            ☎ To-Do: {todoItems.length} call{todoItems.length !== 1 ? "s" : ""} to make
          </h3>
          {todoItems.map((item) => {
            const ref = item.referrer || {};
            const last = item.last_referral;
            return (
              <div key={item.id} style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", padding: "0.6rem 0.75rem", backgroundColor: "white", borderRadius: "6px", border: "1px solid #fde68a", marginBottom: "0.4rem" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {ref.name || "Unknown"}
                    <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "0.85rem" }}>
                      {" "}· {ref.specialty || "—"} · {ref.city || ""}{ref.state ? `, ${ref.state}` : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "#92400e" }}>
                    {last?.last_referral_date
                      ? `Referral from: ${(last.last_referral_date || "").slice(0, 7)} (${last.referral_count} total logged)`
                      : "No referrals logged yet — introduce yourself"}
                    {ref.phone ? ` · ${ref.phone}` : ""}
                  </div>
                  {item.notes && <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{item.notes}</div>}
                </div>
                <button
                  onClick={() => completeTodo(item)}
                  style={{ padding: "0.35rem 0.9rem", backgroundColor: "#16a34a", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}
                >
                  ✓ Called
                </button>
              </div>
            );
          })}
        </div>
      )}

      {errorMessage && (
        <div style={{ marginBottom: "1rem", padding: "0.85rem 1rem", backgroundColor: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: "8px" }}>
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div style={{ marginBottom: "1rem", padding: "0.85rem 1rem", backgroundColor: "#f0fdf4", color: "#166534", border: "1px solid #86efac", borderRadius: "8px" }}>
          {successMessage}
        </div>
      )}

      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        <select
          value={activePlanId || ""}
          disabled={loadingPlans}
          onChange={(e) => {
            setErrorMessage("");
            setSuccessMessage("");
            setActivePlanId(e.target.value ? parseInt(e.target.value, 10) : null);
          }}
          style={{ padding: "0.5rem", borderRadius: "6px", border: "1px solid #d1d5db", minWidth: "200px" }}
        >
          <option value="">Select a plan…</option>
          {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name} ({plan.target_count})</option>)}
        </select>
        {activePlanId && (
          <button
            onClick={() => deletePlan(activePlanId)}
            disabled={busyAction === `delete-plan-${activePlanId}`}
            style={{ padding: "0.5rem 0.75rem", backgroundColor: "#fef2f2", color: "#ef4444", border: "1px solid #fca5a5", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem", opacity: busyAction === `delete-plan-${activePlanId}` ? 0.5 : 1 }}
          >
            {busyAction === `delete-plan-${activePlanId}` ? "Deleting..." : "Delete Plan"}
          </button>
        )}
        <span style={{ color: "#d1d5db" }}>|</span>
        <input
          placeholder="New plan name"
          value={newPlanName}
          onChange={(e) => setNewPlanName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && createPlan()}
          style={{ padding: "0.5rem", borderRadius: "6px", border: "1px solid #d1d5db", width: "200px" }}
        />
        <button onClick={createPlan} disabled={busyAction === "create-plan"} style={{ padding: "0.5rem 1rem", backgroundColor: "#22c55e", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, opacity: busyAction === "create-plan" ? 0.5 : 1 }}>
          {busyAction === "create-plan" ? "Creating..." : "+ Create"}
        </button>
      </div>

      {loadingPlans && <p style={{ color: "#6b7280" }}>Loading outreach plans...</p>}
      {!loadingPlans && plans.length === 0 && (
        <div style={{ marginBottom: "1.5rem", padding: "1rem", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb", color: "#6b7280" }}>
          Create your first plan, then add referrers from Radar or Dashboard.
        </div>
      )}

      {loadingPlan && <p>Loading plan…</p>}

      {activePlan && !loadingPlan && (
        insights && insights.plan_id === activePlan.id && targets.length > 0 ? (
          <>
            {insights.areas?.length > 0 && renderAreas()}
            <h3 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#15803d", marginBottom: "0.5rem" }}>
              Targets by practice
            </h3>
            {renderPracticeGroups()}
          </>
        ) : (
          <>
            {renderTargetGroup("Expand To", expandTargets, "#3b82f6")}
            {renderTargetGroup("Bank / Retain", bankTargets, "#22c55e")}
          </>
        )
      )}

      {!activePlan && !loadingPlan && plans.length > 0 && (
        <div style={{ marginBottom: "2rem", padding: "1rem", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb", color: "#6b7280" }}>
          Select a plan to review and update its outreach targets.
        </div>
      )}

      <div style={{ marginTop: "2rem", padding: "1.25rem", backgroundColor: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#15803d", margin: 0 }}>Outreach Templates</h3>
          <button
            onClick={() => {
              setErrorMessage("");
              setSuccessMessage("");
              setShowTplForm(!showTplForm);
              setEditingTplId(null);
              setTplForm({ name: "", category: "expand", subject: "", body: "" });
            }}
            style={{ padding: "0.4rem 0.75rem", backgroundColor: "#22c55e", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}
          >
            {showTplForm ? "Cancel" : "+ New Template"}
          </button>
        </div>

        {showTplForm && (
          <div style={{ marginBottom: "1rem", padding: "1rem", backgroundColor: "white", borderRadius: "6px", border: "1px solid #d1d5db" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "0.75rem" }}>
              <input placeholder="Template name" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #d1d5db" }} />
              <select value={tplForm.category} onChange={(e) => setTplForm({ ...tplForm, category: e.target.value })} style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #d1d5db" }}>
                <option value="expand">Expand To</option>
                <option value="bank">Bank / Retain</option>
              </select>
            </div>
            <input placeholder="Subject line" value={tplForm.subject} onChange={(e) => setTplForm({ ...tplForm, subject: e.target.value })} style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #d1d5db", width: "100%", marginBottom: "0.75rem", boxSizing: "border-box" }} />
            <textarea placeholder="Body — use {{referrer_name}}, {{practice_name}}, {{your_name}}" value={tplForm.body} onChange={(e) => setTplForm({ ...tplForm, body: e.target.value })} rows={5} style={{ padding: "0.5rem", borderRadius: "4px", border: "1px solid #d1d5db", width: "100%", resize: "vertical", boxSizing: "border-box" }} />
            <button onClick={saveTpl} disabled={busyAction === "save-template"} style={{ marginTop: "0.5rem", padding: "0.5rem 1rem", backgroundColor: "#22c55e", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, opacity: busyAction === "save-template" ? 0.5 : 1 }}>
              {busyAction === "save-template" ? "Saving..." : `${editingTplId ? "Update" : "Save"} Template`}
            </button>
          </div>
        )}

        {loadingTemplates && <p style={{ color: "#6b7280" }}>Loading templates...</p>}
        {!loadingTemplates && templates.length === 0 && !showTplForm && (
          <p style={{ color: "#9ca3af", fontSize: "0.9rem" }}>No templates yet. Create one to streamline your outreach.</p>
        )}

        {templates.map((tpl) => {
          const isDeletingTemplate = busyAction === `delete-template-${tpl.id}`;
          return (
            <div key={tpl.id} style={{ padding: "0.75rem", backgroundColor: "white", borderRadius: "6px", border: "1px solid #e5e7eb", marginBottom: "0.5rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{tpl.name}</span>
                  <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem", padding: "0.15rem 0.5rem", borderRadius: "9999px", backgroundColor: tpl.category === "expand" ? "#dbeafe" : "#dcfce7", color: tpl.category === "expand" ? "#1d4ed8" : "#15803d" }}>
                    {tpl.category === "expand" ? "Expand" : "Bank"}
                  </span>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <button onClick={() => copyTemplate(tpl)} style={{ fontSize: "0.8rem", padding: "0.25rem 0.5rem", backgroundColor: "#eff6ff", color: "#3b82f6", border: "1px solid #bfdbfe", borderRadius: "4px", cursor: "pointer" }}>
                    Copy
                  </button>
                  <button onClick={() => { setTplForm({ name: tpl.name, category: tpl.category, subject: tpl.subject || "", body: tpl.body }); setEditingTplId(tpl.id); setShowTplForm(true); setErrorMessage(""); setSuccessMessage(""); }} style={{ fontSize: "0.8rem", padding: "0.25rem 0.5rem", backgroundColor: "#f9fafb", color: "#374151", border: "1px solid #d1d5db", borderRadius: "4px", cursor: "pointer", fontWeight: 600 }}>
                    Edit
                  </button>
                  <button onClick={() => deleteTpl(tpl.id)} disabled={isDeletingTemplate} style={{ fontSize: "0.8rem", padding: "0.25rem 0.5rem", background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontWeight: 700, opacity: isDeletingTemplate ? 0.5 : 1 }}>
                    ×
                  </button>
                  {copiedTemplate?.id === tpl.id && (
                    <span style={{ fontSize: "0.75rem", color: "#15803d", fontWeight: 600 }}>
                      Copied
                    </span>
                  )}
                </div>
              </div>
              {tpl.subject && <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.25rem" }}>Subject: {tpl.subject}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
